import { get } from 'lodash-es';
import { isEmptyString } from '@/utils/string';

type ErrorWithBody = {
  body?: unknown;
};

type ErrorWithResponse = {
  response?: {
    body?: unknown;
    status?: unknown;
  };
};

type ErrorWithStatus = {
  status?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

export function getApiClientErrorBody(error: unknown): unknown {
  if (!isRecord(error)) return undefined;

  if ('body' in error) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return (error as ErrorWithBody).body;
  }

  if ('response' in error) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const response = (error as ErrorWithResponse).response;
    if (response != null && typeof response === 'object' && 'body' in response) {
      return response.body;
    }
  }

  return undefined;
}

export function getApiClientErrorStatus(error: unknown): number | undefined {
  if (!isRecord(error)) return undefined;

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const status = (error as ErrorWithStatus).status;
  if (typeof status === 'number') return status;

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const responseStatus = (error as ErrorWithResponse).response?.status;
  return typeof responseStatus === 'number' ? responseStatus : undefined;
}

export function isApiClientError(error: unknown): boolean {
  return getApiClientErrorStatus(error) !== undefined;
}

export function isApiClientErrorStatus4xx(error: unknown): boolean {
  const status = getApiClientErrorStatus(error);
  return status != null && status >= 400 && status < 500;
}

const HTTP_CONFLICT = 409;

/**
 * Whether the error is an HTTP 409 Conflict, from either rejection shape the API clients produce.
 *
 * A 409 means the request lost a race rather than that it was malformed, so callers use it to choose
 * wait-and-retry handling — and to withhold a "Try again" action, which would only hit the same
 * conflict.
 */
export function isConflictError(error: unknown): boolean {
  return getApiClientErrorStatus(error) === HTTP_CONFLICT;
}

function nonBlankDetail(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  const detail = String(value);
  return isEmptyString(detail) ? undefined : detail;
}

/**
 * The server-supplied detail from an error body, or `undefined` when there is
 * none. A blank detail counts as none: returning `''` would leave callers
 * rendering an empty error snackbar instead of falling back to their own copy.
 */
export function getApiClientErrorDetail(body: unknown): string | undefined {
  return nonBlankDetail(get(body, 'message')) ?? nonBlankDetail(get(body, 'errors[0].detail'));
}

export function formatApiClientErrorMessage(body: unknown, status: number): string {
  return getApiClientErrorDetail(body) ?? `An unexpected error occurred with HTTP code ${status}`;
}

/**
 * Whether a status's server-supplied detail is written for the user, and so is
 * worth showing instead of the caller's own copy.
 *
 * `ui_api` attaches a detail to *every* error response, but most of them are
 * internal (`services/ui_api/serve/format.go`): 5xx reads
 * `internal error (reference …)`, 404 `not found (reference …)`, 401
 * `unauthorized`. Preferring those would replace copy like "Something went wrong
 * when authenticating with your Okta account" with a reference string.
 *
 * Only the 4xx statuses that carry a request-specific explanation qualify — 400
 * (`BadRequestWithMessage`), 409 conflicts, 422 validation.
 *
 * 429 (`too many requests (reference …)`) is boilerplate too, but is deliberately
 * NOT excluded here. `getUserFacingErrorMessage` special-cases 429 upstream (see
 * `RATE_LIMITED_MESSAGE`), so excluding it would gain nothing there — and it
 * would reroute the direct callers that *don't* go through that helper
 * (`slack-agent/link-error.ts`, `billing/checkout`) into their own non-429
 * fallbacks, which say misleading things like "this link is no longer valid".
 */
function hasActionableDetail(status: number): boolean {
  return status >= 400 && status < 500 && status !== 401 && status !== 404;
}

/**
 * The server-supplied detail for an API error, or `undefined` when the response
 * carried none — or when its detail is one of the internal strings described in
 * `hasActionableDetail`. Unlike `getApiClientErrorMessageFromError` this never
 * invents a generic "HTTP code N" sentence, so callers can fall back to their
 * own copy.
 */
export function getApiClientErrorDetailFromError(error: unknown): string | undefined {
  const status = getApiClientErrorStatus(error);
  if (status === undefined || !hasActionableDetail(status)) return undefined;
  return getApiClientErrorDetail(getApiClientErrorBody(error));
}

export function getApiClientErrorMessageFromError(error: unknown): string | undefined {
  const status = getApiClientErrorStatus(error);
  if (status === undefined) return undefined;
  return formatApiClientErrorMessage(getApiClientErrorBody(error), status);
}

/**
 * Shown when a request is rejected for exceeding the rate limit (HTTP 429).
 * "Try again" copy is wrong here — the fix is to wait, not to retry immediately.
 */
export const RATE_LIMITED_MESSAGE = 'Too many attempts. Wait a moment and try again.';

/**
 * The log-correlation reference `ui_api` bakes into an internal error's detail —
 * `internal error (reference 9f2c)`, `not found (reference …)` — or `undefined`
 * when the detail carries none. It is only ever embedded in the message string
 * (there is no structured field), so it has to be parsed back out.
 */
function extractErrorReference(error: unknown): string | undefined {
  const detail = getApiClientErrorDetail(getApiClientErrorBody(error));
  if (detail == null) return undefined;
  return /\(reference ([^)]+)\)/i.exec(detail)?.[1];
}

/**
 * The message to show a user when an action fails:
 * - an actionable server detail (400/409/422 — see `hasActionableDetail`), which
 *   is already human-readable, verbatim;
 * - dedicated copy for rate limiting (429);
 * - otherwise the caller's own `fallback`, with the server's log-correlation
 *   reference appended as a `Ref: …` support suffix when the response carried
 *   one (5xx / 404), so a failed request stays traceable to server logs.
 *
 * The `Ref: …` suffix is a stopgap: mutation errors are not yet captured to
 * observability, so the toast is currently the only place that reference is
 * visible. Once mutation failures are reported to Sentry with the reference
 * intact, drop the suffix — support will read it from there instead. See
 * FPS-3347.
 *
 * This is the default for any user-facing error snackbar. Prefer it over
 * `getApiClientErrorMessageFromError(error) ?? fallback`: that pattern looks like
 * it falls back to `fallback`, but `getApiClientErrorMessageFromError` returns a
 * value for *every* API error — so the caller's copy is never reached.
 * `getApiClientErrorMessageFromError` is for developer-facing surfaces (logs,
 * dev tools) that want a raw string at any cost.
 */
export function getUserFacingErrorMessage(error: unknown, fallback: string): string {
  if (getApiClientErrorStatus(error) === 429) return RATE_LIMITED_MESSAGE;

  const actionableDetail = getApiClientErrorDetailFromError(error);
  if (actionableDetail != null) return actionableDetail;

  const reference = extractErrorReference(error);
  return reference == null ? fallback : `${fallback} Ref: ${reference}`;
}
